import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { ChevronDown, FileText, Search, X } from "lucide-react";

import { getTasks } from "../../../services/taskServices/TaskService";

export interface CalendarTaskSelection {
  id: string | number;
  friendlyId?: string | number;
  title: string;
  participant?: {
    id?: number | string;
    firstName?: string;
    lastName?: string;
  } | null;
  organization?: {
    abbreviation?: string;
  } | null;
  assignees?: any[];
}

interface AsyncTaskDropdownProps {
  authToken: string;
  label?: string;
  selectedTask?: CalendarTaskSelection | null;
  participantId?: string | null;
  onChange: (task: CalendarTaskSelection | null) => void;
  disabled?: boolean;
  debounceMs?: number;
  pageSize?: number;
}

const getParticipantName = (task?: CalendarTaskSelection | null): string => {
  const participant = task?.participant;
  if (!participant) return "No participant";

  const fullName = `${participant.firstName ?? ""} ${participant.lastName ?? ""}`.trim();
  return fullName || `Participant ${participant.id ?? ""}`.trim();
};

const getTaskReference = (task?: CalendarTaskSelection | null): string => {
  if (!task) return "";
  const abbreviation = task.organization?.abbreviation;
  const friendlyId = task.friendlyId ?? task.id;
  return abbreviation ? `${abbreviation}-${friendlyId}` : `#${friendlyId}`;
};

const AsyncTaskDropdown: React.FC<AsyncTaskDropdownProps> = ({
  authToken,
  label = "Link to Task",
  selectedTask,
  participantId,
  onChange,
  disabled = false,
  debounceMs = 300,
  pageSize = 20,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<CalendarTaskSelection[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizedSearch = searchTerm.trim();
  const canSearch = normalizedSearch.length >= 2;

  const openDropdown = () => {
    if (disabled || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setResults([]);
      setSearchError(null);
      return;
    }

    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleInteraction = (event: Event) => {
      if (portalRef.current?.contains(event.target as Node)) return;
      if (
        event.type === "mousedown" &&
        triggerRef.current?.contains(event.target as Node)
      ) {
        return;
      }

      setIsOpen(false);
    };

    window.addEventListener("scroll", handleInteraction, true);
    window.addEventListener("resize", handleInteraction);
    document.addEventListener("mousedown", handleInteraction);

    return () => {
      window.removeEventListener("scroll", handleInteraction, true);
      window.removeEventListener("resize", handleInteraction);
      document.removeEventListener("mousedown", handleInteraction);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !authToken || !canSearch) {
      setResults([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setIsSearching(true);
      setSearchError(null);

      try {
        const filters = participantId
          ? { participants: [participantId] }
          : undefined;

        const response = await getTasks(
          authToken,
          undefined,
          1,
          pageSize,
          filters,
          "",
          false,
          false,
          false,
          normalizedSearch,
          controller.signal
        );

        if (!controller.signal.aborted) {
          setResults(response?.tasks ?? []);
        }
      } catch (error) {
        if (
          !controller.signal.aborted &&
          !(error instanceof DOMException && error.name === "AbortError")
        ) {
          console.error("Failed to search calendar-linkable tasks.", error);
          setSearchError("We couldn't search tasks. Please try again.");
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    authToken,
    canSearch,
    debounceMs,
    isOpen,
    normalizedSearch,
    pageSize,
    participantId,
  ]);

  const uniqueResults = useMemo(() => {
    const byId = new Map<string, CalendarTaskSelection>();

    for (const task of results) {
      byId.set(String(task.id), task);
    }

    if (selectedTask) {
      byId.set(String(selectedTask.id), selectedTask);
    }

    return Array.from(byId.values());
  }, [results, selectedTask]);

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label && (
        <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-500">
          {label}
        </label>
      )}

      <div
        ref={triggerRef}
        onClick={() => (isOpen ? setIsOpen(false) : openDropdown())}
        className={`relative flex h-12 items-center rounded-xl border bg-gray-50/50 transition-all duration-200 ${
          disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-100 opacity-60"
            : isOpen
              ? "cursor-pointer border-[#7E57C2] bg-white ring-4 ring-[#7E57C2]/10"
              : "cursor-pointer border-gray-200 hover:border-gray-300"
        }`}
      >
        <FileText
          size={18}
          className={`absolute left-3.5 ${
            isOpen ? "text-[#7E57C2]" : "text-gray-400"
          }`}
        />

        <div className="min-w-0 flex-1 pl-11 pr-16">
          {selectedTask ? (
            <>
              <div className="truncate text-sm font-semibold text-gray-900">
                {selectedTask.title}
              </div>
              <div className="truncate text-[10px] font-bold uppercase tracking-wider text-[#7E57C2]">
                {getTaskReference(selectedTask)} • {getParticipantName(selectedTask)}
              </div>
            </>
          ) : (
            <span className="text-sm text-gray-400">Search for a task…</span>
          )}
        </div>

        <div className="absolute right-3 flex items-center gap-1">
          {selectedTask && !disabled && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onChange(null);
                setIsOpen(false);
              }}
              className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label="Unlink task"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={18}
            className={`text-gray-400 transition-transform duration-300 ${
              isOpen ? "rotate-180 text-[#7E57C2]" : ""
            }`}
          />
        </div>
      </div>

      {isOpen &&
        !disabled &&
        ReactDOM.createPortal(
          <div
            ref={portalRef}
            className="async-task-popup dark-popup fixed z-[999999] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-2xl"
            style={{
              top: coords.top,
              left: coords.left,
              width: Math.max(coords.width, 340),
            }}
          >
            <div className="border-b border-gray-50 bg-gray-50/50 p-2">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  placeholder="Search by task title or ID…"
                  className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-4 text-sm outline-none transition-all focus:border-[#7E57C2] focus:ring-2 focus:ring-[#7E57C2]/10"
                />
              </div>
              {participantId && (
                <div className="mt-1.5 px-1 text-[10px] font-medium text-[#7E57C2]">
                  Search is restricted to tasks for the selected participant.
                </div>
              )}
            </div>

            <div className="max-h-[260px] overflow-y-auto py-1">
              {!canSearch ? (
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                  Type at least 2 characters to search tasks.
                </div>
              ) : isSearching ? (
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                  Searching tasks…
                </div>
              ) : searchError ? (
                <div className="px-4 py-6 text-center text-sm text-red-500">
                  {searchError}
                </div>
              ) : uniqueResults.length > 0 ? (
                uniqueResults.map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => {
                      onChange(task);
                      setIsOpen(false);
                    }}
                    className="flex w-full flex-col gap-0.5 px-4 py-2 text-left transition-colors hover:bg-[#F9F6FF]"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#7E57C2]">
                      {getTaskReference(task)} • {getParticipantName(task)}
                    </span>
                    <span className="truncate text-sm font-semibold text-gray-700">
                      {task.title}
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-sm italic text-gray-400">
                  No matching tasks found.
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AsyncTaskDropdown;
